import { Cancel, CheckCircle, Person, Schedule } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import React from 'react';

import { useComiteeTabContext } from './ComiteeTabProvider';
import { ApprovalRoleCardProps } from './types';

const ApprovalRoleCard = ({
  role,
  index,
  onApprove,
  onReject,
  canEdit,
}: ApprovalRoleCardProps) => {
  const { requestType, requestId, comiteeType, selectedRoleId } =
    useComiteeTabContext();

  const isSubmitting =
    useIsMutating({
      mutationKey: ['approvals', 'submit', requestType, requestId, comiteeType],
    }) > 0;

  const isSelectedCard = selectedRoleId === role.roleId;
  const isLoadingCard = isSelectedCard && isSubmitting;

  const isRejected = role.rejected.length > 0;
  const isApproved = role.approved.length >= role.requiredApprovals;

  const getStatusColor = () => {
    if (isRejected) {
      return 'error';
    }
    if (isApproved) return 'success';
    return 'warning';
  };
  const getStatusLabel = () => {
    if (isRejected) {
      return 'Rechazado';
    }
    if (isApproved) return 'Aprobado';
    return 'Pendiente';
  };

  const getStatusIcon = () => {
    if (isRejected) {
      return <Cancel sx={{ fontSize: '1.5rem', color: 'error.main' }} />;
    }
    if (isApproved) {
      return <CheckCircle sx={{ fontSize: '1.5rem', color: 'success.main' }} />;
    }
    return <Schedule sx={{ fontSize: '1.5rem', color: 'warning.main' }} />;
  };

  const getBackgroundColor = () => {
    if (isRejected) {
      return 'rgba(244, 67, 54, 0.15)';
    }
    if (isApproved) return 'rgba(46, 125, 50, 0.15)';
    return 'rgba(237, 108, 2, 0.15)';
  };

  const DeleteLoadingButton = () => {
    return (
      <IconButton
        disabled
        sx={{
          width: 40,
          height: 40,
          cursor: 'default',
        }}
      >
        <CircularProgress
          size={18}
          thickness={5}
          sx={{
            color: 'primary',
          }}
        />
      </IconButton>
    );
  };

  return (
    <Paper
      key={index}
      sx={{
        p: 2,
        mb: 2,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '20%' }}>
        <Box
          sx={{
            backgroundColor: getBackgroundColor(),
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getStatusIcon()}
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              lineHeight: 1,
              fontSize: { xs: '0.85rem' },
            }}
          >
            {role.roleName}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.75rem',
              lineHeight: 1,
            }}
          >
            Rol de Aprobación
          </Typography>
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Chip
        label={getStatusLabel()}
        color={getStatusColor()}
        size="small"
        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
            p: 0.75,
            borderRadius: '50%',
            mr: 1,
          }}
        >
          <Person sx={{ fontSize: '0.95rem', color: 'purple.main' }} />
        </Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1 }}
        >
          {role.approved.length}/{role.requiredApprovals}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.65rem', ml: 0.5 }}
        >
          Aprobaciones
        </Typography>
      </Box>

      {/* Show approved names */}
      {role.approved.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 120,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                p: 0.75,
                borderRadius: '50%',
                mr: 1,
              }}
            >
              <CheckCircle
                sx={{ fontSize: '0.95rem', color: 'success.main' }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1 }}
            >
              {role.approved.map((a) => a.user.name ?? a.user.email).join(', ')}
            </Typography>
          </Box>
        </>
      )}

      {role.rejected.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 120,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                p: 0.75,
                borderRadius: '50%',
                mr: 1,
              }}
            >
              <Cancel sx={{ fontSize: '0.95rem', color: 'error.main' }} />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1 }}
            >
              {role.rejected.map((a) => a.user.name ?? a.user.email).join(', ')}
            </Typography>
          </Box>
        </>
      )}

      {canEdit && role.userCanApprove && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              ml: 'auto',
            }}
          >
            {isLoadingCard ? (
              <DeleteLoadingButton />
            ) : (
              <>
                <Tooltip title="Aprobar solicitud" arrow>
                  <IconButton
                    onClick={() => onApprove(role.roleId)}
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      color: 'success.main',
                      '&:hover': {
                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rechazar solicitud" arrow>
                  <IconButton
                    onClick={() => onReject(role.roleId)}
                    sx={{
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.2)',
                      },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <Cancel sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ApprovalRoleCard;
